function escapeHtml(value) {
    return String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
}

function numberValue(value) {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
}

function fmt(value, digits = 0, suffix = "") {
    const n = Number(value);
    if (!Number.isFinite(n)) return "-";
    return `${digits > 0 ? n.toFixed(digits) : Math.round(n)}${suffix}`;
}

function fmtMbps(value) {
    const n = Number(value);
    if (!Number.isFinite(n) || n <= 0) return "-";
    return `${Math.round(n)} Mbps`;
}

function plural(value, singular, pluralText = `${singular}s`) {
    return Number(value) === 1 ? singular : pluralText;
}

function progressWidth(count, required) {
    const c = Math.max(0, numberValue(count));
    const r = Math.max(1, numberValue(required));
    return Math.min(100, Math.round((c / r) * 100));
}

export function recommendationMeta(recommendation) {
    switch (recommendation) {
        case "ReallyNeedsUpgrade":
            return {
                badge: "text-bg-danger",
                icon: "fa-triangle-exclamation",
                title: "Upgrade recommended",
                summary: "Peak/max usage crossed the upgrade threshold often enough to count as regular saturation.",
            };
        case "CouldUseUpgrade":
            return {
                badge: "text-bg-warning",
                icon: "fa-arrow-up-right-dots",
                title: "Watch for upgrade",
                summary: "Median usage crossed the sustained-pressure threshold often enough to justify upgrade review.",
            };
        case "NoUpgradeRecommended":
            return {
                badge: "text-bg-success",
                icon: "fa-circle-check",
                title: "No upgrade recommended",
                summary: "Usage did not cross the upgrade thresholds often enough in the selected period.",
            };
        case "NoUsablePlanLimits":
            return {
                badge: "text-bg-warning",
                icon: "fa-gauge-high",
                title: "Plan limits unavailable",
                summary: "Insight needs a usable max download or upload plan limit before it can judge upgrade pressure.",
            };
        case "AnalysisUnavailable":
            return {
                badge: "text-bg-warning",
                icon: "fa-triangle-exclamation",
                title: "Analysis unavailable",
                summary: "Insight could not complete the upgrade analysis for this circuit. Try refreshing or widening the time period.",
            };
        default:
            return {
                badge: "text-bg-secondary",
                icon: "fa-circle-info",
                title: "Not enough data",
                summary: "No usable traffic buckets were found for this circuit in the selected period.",
            };
    }
}

function metric(label, value, icon) {
    return `
        <div class="col-sm-6 col-xl-3">
            <div class="border rounded-2 p-2 h-100">
                <div class="text-muted small"><i class="fa ${icon} me-1"></i>${escapeHtml(label)}</div>
                <div class="fw-semibold text-body">${escapeHtml(value)}</div>
            </div>
        </div>
    `;
}

function bucketTest(label, count, percent, threshold, required, tone, description) {
    const width = progressWidth(count, required);
    const bar = tone === "danger" ? "bg-danger" : "bg-warning";
    const badge = tone === "danger" ? "text-bg-danger" : "text-bg-warning";
    const ariaValueText = `${fmt(count)} qualifying buckets out of ${fmt(required)} required at ${fmt(threshold, 0, "%")} threshold; ${fmt(percent, 1, "%")} of valid buckets qualified.`;
    return `
        <div class="col-lg-6">
            <div class="border rounded-2 p-2 h-100">
                <div class="d-flex justify-content-between align-items-start gap-2">
                    <div>
                        <div class="fw-semibold">${escapeHtml(label)}</div>
                        <div class="text-muted small">${escapeHtml(description)}</div>
                    </div>
                    <span class="badge rounded-pill ${badge}">${fmt(count)} / ${fmt(required)}</span>
                </div>
                <div class="progress mt-2" style="height: 8px;" role="progressbar" aria-label="${escapeHtml(label)} qualifying buckets" aria-valuenow="${width}" aria-valuemin="0" aria-valuemax="100" aria-valuetext="${escapeHtml(ariaValueText)}">
                    <div class="progress-bar ${bar}" style="width: ${width}%"></div>
                </div>
                <div class="text-muted small mt-1">${fmt(percent, 1, "%")} of valid buckets reached at least ${fmt(threshold, 0, "%")}</div>
            </div>
        </div>
    `;
}

export function renderCircuitUpgradeAnalysis(target, payload, options = {}) {
    if (!target) return;
    const data = payload || {};
    const meta = recommendationMeta(data.recommendation);
    // Neutral summary badge (strip/modal); the bucket-test badges below keep
    // their severity colours.
    const badgeClass = options.neutralBadge ? "text-bg-secondary" : meta.badge;
    const badgeIcon = options.neutralBadge ? "fa-circle-info" : meta.icon;
    const validBuckets = numberValue(data.valid_bucket_count);
    const requiredBuckets = numberValue(data.required_bucket_count);
    const bucketInterval = data.bucket_interval ? escapeHtml(data.bucket_interval) : "-";
    const scope = data.all_shapers
        ? `${fmt(data.observed_shaper_count)} observed ${plural(data.observed_shaper_count, "shaper")}`
        : "Selected shaper";

    target.innerHTML = `
        <div class="visually-hidden" role="status" aria-live="polite">
            ${escapeHtml(summarizeCircuitUpgradeAnalysis(data))}
        </div>
        <div class="px-3 pb-3">
            <div class="d-flex flex-wrap justify-content-between align-items-start gap-3">
                <div>
                    <span class="badge rounded-pill ${badgeClass}">
                        <i class="fa ${badgeIcon} me-1"></i>${escapeHtml(meta.title)}
                    </span>
                    <div class="mt-2 fw-semibold">${escapeHtml(data.circuit_name || `Circuit ${data.circuit_hash || ""}`.trim())}</div>
                    <div class="text-muted small">${escapeHtml(meta.summary)}</div>
                </div>
                <div class="text-muted small text-start text-lg-end">
                    <div><i class="fa fa-clock me-1"></i>Time bucket: ${bucketInterval}</div>
                    <div><i class="fa fa-filter me-1"></i>Rule: ${fmt(requiredBuckets)} of ${fmt(validBuckets)} ${plural(validBuckets, "bucket")} (${fmt(numberValue(data.regular_bucket_fraction) * 100, 0, "%")})</div>
                    <div><i class="fa fa-network-wired me-1"></i>${escapeHtml(scope)}</div>
                </div>
            </div>

            <div class="row g-2 mt-2">
                ${metric("Plan limit", `DL ${fmtMbps(data.download_limit_mbps)} / UL ${fmtMbps(data.upload_limit_mbps)}`, "fa-gauge")}
                ${metric("Peak pressure", `P90 ${fmt(data.p90_peak_utilization_percent, 0, "%")} / max ${fmt(data.max_peak_utilization_percent, 0, "%")}`, "fa-arrow-up")}
                ${metric("Median pressure", `P50 ${fmt(data.p50_median_utilization_percent, 0, "%")} / max ${fmt(data.max_median_utilization_percent, 0, "%")}`, "fa-chart-line")}
                ${metric("Valid buckets", `${fmt(validBuckets)} observed`, "fa-table-cells")}
            </div>

            <div class="row g-2 mt-2">
                ${bucketTest(
                    "Peak/max bucket test",
                    data.peak_bucket_count,
                    data.peak_bucket_percent,
                    data.peak_threshold_percent,
                    requiredBuckets,
                    "danger",
                    "Urgent when peak/max usage is regularly near the service limit."
                )}
                ${bucketTest(
                    "Median bucket test",
                    data.median_bucket_count,
                    data.median_bucket_percent,
                    data.median_threshold_percent,
                    requiredBuckets,
                    "warn",
                    "Watch when sustained median usage is regularly high."
                )}
            </div>
        </div>
    `;
}

export function summarizeCircuitUpgradeAnalysis(payload) {
    const data = payload || {};
    const meta = recommendationMeta(data.recommendation);
    return `${meta.title}: ${fmt(data.peak_bucket_count)} peak buckets and ${fmt(data.median_bucket_count)} median buckets qualified out of ${fmt(data.valid_bucket_count)} valid ${plural(data.valid_bucket_count, "bucket")}.`;
}
